/**
 * StrategyDNASection - Strategy DNA Collapsible Container
 *
 * Main container for Strategy DNA display in the QuantX panel.
 * Contains DNASummaryBar, LevelStatsTable, and FakeoutWarningsPanel.
 *
 * Total Height: ~250px when expanded
 */

import React, { useState, memo, useCallback } from 'react';
import { Box, Typography, Collapse, CircularProgress, Tooltip, Modal } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DnaIcon from '@mui/icons-material/BiotechOutlined';

import useStrategyDNA from '../../../hooks/useStrategyDNA';
import DNASummaryBar from './DNASummaryBar';
import LevelStatsTable from './LevelStatsTable';
import FakeoutWarningsPanel from './FakeoutWarningsPanel';

/**
 * Event Details Modal - Simple drill-down view
 */
const EventDetailsModal = memo(({ open, onClose, levelStat, events = [], loading }) => {
    if (!open) return null;

    const levelLabel = levelStat
        ? `${levelStat.levelType} ${levelStat.levelValue || ''}`.trim()
        : 'Level Details';

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <Box
                sx={{
                    width: '500px',
                    maxHeight: '400px',
                    backgroundColor: '#0f0f0f',
                    border: '1px solid #374151',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        backgroundColor: '#0a0a0a',
                        borderBottom: '1px solid #374151'
                    }}
                >
                    <Typography sx={{ fontSize: '12px', color: '#fff', fontWeight: 700, fontFamily: 'monospace' }}>
                        {levelLabel} Events
                    </Typography>
                    <Typography
                        onClick={onClose}
                        sx={{ fontSize: '14px', color: '#6b7280', cursor: 'pointer', '&:hover': { color: '#fff' } }}
                    >
                        X
                    </Typography>
                </Box>

                {/* Content */}
                <Box sx={{ padding: '12px', maxHeight: '340px', overflowY: 'auto' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <CircularProgress size={24} sx={{ color: '#dc2626' }} />
                        </Box>
                    ) : events.length === 0 ? (
                        <Typography sx={{ fontSize: '10px', color: '#6b7280', textAlign: 'center' }}>
                            No events found for this level
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {/* Event list */}
                            {events.map((event, i) => (
                                <Box
                                    key={event.id || i}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '6px 8px',
                                        backgroundColor: 'rgba(55, 65, 81, 0.1)',
                                        borderRadius: '2px',
                                        borderLeft: `2px solid ${event.outcome === 'BOUNCE' ? '#22c55e' :
                                            event.outcome === 'BREAK' ? '#fbbf24' :
                                                event.outcome === 'FAKEOUT' ? '#ef4444' : '#6b7280'
                                            }`
                                    }}
                                >
                                    <Box>
                                        <Typography sx={{ fontSize: '10px', color: '#fff', fontFamily: 'monospace' }}>
                                            {new Date(event.createdAt).toLocaleString()}
                                        </Typography>
                                        <Typography sx={{ fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace' }}>
                                            {event.session || '--'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography
                                            sx={{
                                                fontSize: '10px',
                                                fontWeight: 700,
                                                fontFamily: 'monospace',
                                                color: event.outcome === 'BOUNCE' ? '#22c55e' :
                                                    event.outcome === 'BREAK' ? '#fbbf24' :
                                                        event.outcome === 'FAKEOUT' ? '#ef4444' : '#6b7280'
                                            }}
                                        >
                                            {event.outcome || 'PENDING'}
                                        </Typography>
                                        {event.mfePips != null && (
                                            <Typography sx={{ fontSize: '9px', color: '#22c55e', fontFamily: 'monospace' }}>
                                                MFE: +{event.mfePips?.toFixed(1)}
                                            </Typography>
                                        )}
                                        {event.maePips != null && (
                                            <Typography sx={{ fontSize: '9px', color: '#ef4444', fontFamily: 'monospace' }}>
                                                MAE: {event.maePips?.toFixed(1)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </Modal>
    );
});

EventDetailsModal.displayName = 'EventDetailsModal';

/**
 * StrategyDNASection Component
 */
const StrategyDNASection = memo(({
    symbol,
    token,
    defaultExpanded = false,
    refreshInterval = 60000
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [modalEvents, setModalEvents] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);

    // Use Strategy DNA hook
    const {
        summary,
        stats,
        highFakeoutWarnings,
        loading,
        isRefreshing,
        hasData,
        error,
        refresh,
        fetchEvents
    } = useStrategyDNA({
        symbol,
        token,
        autoRefresh: true,
        refreshInterval,
        enabled: !!token && !!symbol
    });

    // Handle row click for drill-down
    const handleRowClick = useCallback(async (levelStat) => {
        setSelectedLevel(levelStat);
        setModalOpen(true);
        setModalLoading(true);
        setModalEvents([]);

        try {
            const eventsData = await fetchEvents({
                levelType: levelStat.levelType,
                levelValue: levelStat.levelValue,
                limit: 20
            });
            setModalEvents(eventsData?.events || []);
        } catch (err) {
            console.error('[StrategyDNASection] Failed to fetch events:', err);
        } finally {
            setModalLoading(false);
        }
    }, [fetchEvents]);

    // Handle warning click
    const handleWarningClick = useCallback((warning) => {
        handleRowClick(warning);
    }, [handleRowClick]);

    // Close modal
    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        setSelectedLevel(null);
        setModalEvents([]);
    }, []);

    // Toggle expanded
    const toggleExpanded = useCallback(() => {
        setExpanded(prev => !prev);
    }, []);

    return (
        <Box sx={{ marginTop: 1 }}>
            {/* Section Header */}
            <Box
                onClick={toggleExpanded}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 10px',
                    backgroundColor: '#0a0a0a',
                    borderRadius: expanded ? '3px 3px 0 0' : '3px',
                    border: '1px solid rgba(55, 65, 81, 0.3)',
                    borderBottom: expanded ? 'none' : '1px solid rgba(55, 65, 81, 0.3)',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#1a1a1a' }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DnaIcon sx={{ fontSize: 14, color: '#dc2626' }} />
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#dc2626',
                            fontFamily: 'monospace',
                            letterSpacing: '0.5px'
                        }}
                    >
                        STRATEGY DNA
                    </Typography>

                    {/* Warning badge if fakeout warnings exist */}
                    {highFakeoutWarnings.length > 0 && (
                        <Tooltip title={`${highFakeoutWarnings.length} fakeout warnings`}>
                            <Box
                                sx={{
                                    backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                    color: '#ef4444',
                                    padding: '1px 6px',
                                    borderRadius: '8px',
                                    fontSize: '9px',
                                    fontWeight: 700,
                                    fontFamily: 'monospace'
                                }}
                            >
                                {highFakeoutWarnings.length}!
                            </Box>
                        </Tooltip>
                    )}

                    {/* Loading indicator */}
                    {(loading || isRefreshing) && (
                        <CircularProgress size={10} sx={{ color: '#dc2626' }} />
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {hasData && (
                        <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: 'monospace' }}>
                            {stats.length} levels
                        </Typography>
                    )}
                    <ExpandMoreIcon
                        sx={{
                            fontSize: 16,
                            color: '#6b7280',
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s'
                        }}
                    />
                </Box>
            </Box>

            {/* Collapsible Content */}
            <Collapse in={expanded}>
                <Box
                    sx={{
                        backgroundColor: '#0f0f0f',
                        border: '1px solid rgba(55, 65, 81, 0.3)',
                        borderTop: 'none',
                        borderRadius: '0 0 3px 3px',
                        padding: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                    }}
                >
                    {/* Error State */}
                    {error && (
                        <Box
                            sx={{
                                padding: '8px',
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                borderRadius: '2px'
                            }}
                        >
                            <Typography sx={{ fontSize: '10px', color: '#ef4444', fontFamily: 'monospace' }}>
                                Error: {error}
                            </Typography>
                        </Box>
                    )}

                    {/* Loading State */}
                    {loading && !hasData && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <CircularProgress size={24} sx={{ color: '#dc2626' }} />
                        </Box>
                    )}

                    {/* Content */}
                    {hasData && (
                        <>
                            {/* Summary Bar */}
                            <DNASummaryBar
                                summary={summary}
                                loading={isRefreshing}
                                onRefresh={refresh}
                                totalEvents={summary?.totalEvents}
                            />

                            {/* Fakeout Warnings */}
                            {highFakeoutWarnings.length > 0 && (
                                <FakeoutWarningsPanel
                                    warnings={highFakeoutWarnings}
                                    onWarningClick={handleWarningClick}
                                    maxVisible={3}
                                />
                            )}

                            {/* Level Stats Table */}
                            <LevelStatsTable
                                stats={stats}
                                onRowClick={handleRowClick}
                                maxHeight={120}
                            />
                        </>
                    )}

                    {/* No Data State */}
                    {!loading && !hasData && !error && (
                        <Box sx={{ padding: '16px', textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>
                                No Strategy DNA data available for {symbol}
                            </Typography>
                            <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: 'monospace', marginTop: 0.5 }}>
                                Events will appear as price interacts with key levels
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Collapse>

            {/* Event Details Modal */}
            <EventDetailsModal
                open={modalOpen}
                onClose={handleCloseModal}
                levelStat={selectedLevel}
                events={modalEvents}
                loading={modalLoading}
            />
        </Box>
    );
});

StrategyDNASection.displayName = 'StrategyDNASection';

export default StrategyDNASection;
